import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { streamingAccountService, Slot } from "@/services/streaming-account";
import { Loader2 } from "lucide-react";

interface UpdateSlotModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slot: Slot | null;
  accountId?: string | number;
}

export function UpdateSlotModal({
  open,
  onOpenChange,
  slot,
  accountId,
}: UpdateSlotModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (open && slot) {
      setName(slot.name);
      setPin(slot.pin || "");
    }
  }, [open, slot]);

  const updateSlotMutation = useMutation({
    mutationFn: (data: { name: string; pin?: string }) =>
      streamingAccountService.updateSlot(slot!.id, data),
    onSuccess: () => {
      toast.success("Slot actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["streaming-accounts"] });
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: ["streaming-account", String(accountId)] });
      }
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("El nombre del slot es requerido");
      return;
    }
    updateSlotMutation.mutate({ name: name.trim(), pin: pin.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Actualizar Slot</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="slotName">Nombre del Slot</Label>
              <Input
                id="slotName"
                placeholder="Ej: Perfil 1, Usuario 2, etc."
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slotPin">PIN</Label>
              <Input
                id="slotPin"
                placeholder="Ej: 1234"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={updateSlotMutation.isPending}>
              {updateSlotMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Actualizar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
